// Ruta: frontend/src/modules/checklists/pages/ChecklistListPage.tsx
import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useChecklist } from '@core/hooks/useChecklist';
import { Button } from '@shared/components/Button';
import { Checklist } from '@core/types';
import QRCode from 'react-qr-code';

const ChecklistListPage: React.FC = () => {
  const { checklists, loadChecklists, deleteChecklist, isLoading } = useChecklist();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadChecklists();
  }, [location.key]);

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este checklist?')) return;
    await deleteChecklist(id);
  };

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Cargando checklists...</div>;
  }

  const checklistList = checklists || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Checklists</h1>
        <Button onClick={() => navigate('/checklists/create')}>Crear nuevo</Button>
      </div>

      {checklistList.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">No hay checklists creados</p>
          <Button className="mt-4" onClick={() => navigate('/checklists/create')}>
            Crear el primero
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Scroll horizontal solo si es necesario */}
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Preguntas
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    QR
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {checklistList.map((checklist: Checklist) => (
                  <tr key={checklist.id}>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{checklist.title}</div>
                      {checklist.description && (
                        <div className="text-sm text-gray-500 truncate max-w-[150px] sm:max-w-xs">
                          {checklist.description}
                        </div>
                      )}
                      {/* Mostrar número de preguntas en móvil debajo del título */}
                      <div className="sm:hidden text-xs text-gray-500 mt-1">
                        {checklist.questions?.length || 0} preguntas
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 text-center hidden sm:table-cell">
                      {checklist.questions?.length || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                      {checklist.qrCode ? (
                        <QRCode value={checklist.qrCode} size={40} />
                      ) : (
                        'Sin QR'
                      )}
                    </td>
                    <td className="px-4 py-4">
                      {/* ✅ Botones apilados verticalmente en móvil, en fila en pantallas grandes */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                        <Link
                          to={`/checklists/${checklist.id}/edit`}
                          className="text-blue-600 hover:text-blue-900 text-sm sm:text-base whitespace-nowrap"
                        >
                          Editar
                        </Link>
                        <button
                          onClick={() => handleDelete(checklist.id)}
                          className="text-red-600 hover:text-red-900 text-sm sm:text-base whitespace-nowrap"
                        >
                          Eliminar
                        </button>
                        <Link
                          to={`/checklists/${checklist.id}`}
                          className="text-green-600 hover:text-green-900 text-sm sm:text-base whitespace-nowrap"
                        >
                          Ver
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistListPage;